import { useQueryClient } from '@tanstack/react-query'
import { Button, Flex, Form, message, Spin } from 'antd'
import dayjs from 'dayjs'
import { useState } from 'react'
import { useParams } from 'react-router-dom'

import {
  useTreatmentServiceGetListPregnancyMonitoringInvestigationsEndpoint,
  useTreatmentServiceGetPregnancyMonitoringEndpoint,
  useTreatmentServiceGetPregnancyMonitoringEndpointKey,
  useTreatmentServiceGetPregnancyMonitoringListEndpointKey,
  useTreatmentServiceUpdatePregnancyMonitoringEndpoint
} from 'src/api/queries'
import { ApiError } from 'src/api/requests'
import { FormFooter, PregnancyInvestigationsCard } from 'src/components'
import { ConsultationPregnancyCard } from 'src/features/persons/components'
import { yearMonthDaySlash } from 'src/libs'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

export const PersonPregnancyDetails = () => {
  const { id } = useParams()
  const { t } = useLocales()
  const queryClient = useQueryClient()

  const [form] = Form.useForm()

  const [isEditing, setIsEditing] = useState(false)

  const { data, isLoading } = useTreatmentServiceGetPregnancyMonitoringEndpoint(
    {
      id: String(id)
    },
    [String(id)],
    { enabled: !!id }
  )

  const { data: investigations } =
    useTreatmentServiceGetListPregnancyMonitoringInvestigationsEndpoint({
      pageNumber: 1,
      ids: [String(id)]
    })

  const { mutateAsync: updatePregnancy, isPending: isUpdatePending } =
    useTreatmentServiceUpdatePregnancyMonitoringEndpoint({
      onError: (err: ApiError) => formErrorHandler(err.body.errors),
      onSuccess: () => {
        onSuccess()
        message.success(t('pregnancy.pregnancy_edit_successfully'))
      }
    })

  const onToggle = () => setIsEditing((prev) => !prev)

  const onSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: [useTreatmentServiceGetPregnancyMonitoringEndpointKey]
    })

    queryClient.invalidateQueries({
      queryKey: [useTreatmentServiceGetPregnancyMonitoringListEndpointKey]
    })
    onToggle()
  }

  const onSubmit = async () => {
    const values = await form.validateFields()

    updatePregnancy({
      id: String(id),
      requestBody: {
        interruptedPregnancyReason: values?.interruptedPregnancyReason,
        pregnancyActionStatus: values?.pregnancyActionStatus,
        pregnancyStatusChangedDate: dayjs(
          values?.pregnancyStatusChangedDate
        ).format(yearMonthDaySlash),
        pregnancyStatusChangedComment: values?.pregnancyStatusChangedComment
      }
    })
  }

  return (
    <Spin spinning={isLoading}>
      <Flex vertical gap={16}>
        {!isEditing && (
          <Flex justify="end" gap={8}>
            <Button type="primary" onClick={onToggle}>
              {t('common.edit')}
            </Button>
          </Flex>
        )}

        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
          disabled={!isEditing}
        >
          <ConsultationPregnancyCard
            isOnDetailsModal
            data={data}
            form={form}
            isEditing={isEditing}
          />

          <FormFooter
            hidden={!isEditing}
            onCancel={onToggle}
            onSave={onSubmit}
            saveConfirmTitle={`${t('persons.save')}?`}
            saveLabel={t('persons.save')}
            loading={isUpdatePending}
          />
        </Form>
        {id && (
          <PregnancyInvestigationsCard
            id={id}
            investigations={investigations}
          />
        )}
      </Flex>
    </Spin>
  )
}
